const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export const UI_VERSION = '1.2.16';

export async function getApiHealth(): Promise<{ status: string; version: string }> {
  const resp = await fetch(`${API_BASE}/health`);
  return resp.json();
}

// Token refresh callback — set by the app to enable automatic 401 retry
let _tokenRefreshFn: (() => Promise<string>) | null = null;
export function setTokenRefreshFn(fn: () => Promise<string>) {
  _tokenRefreshFn = fn;
}

async function request<T>(
  path: string,
  options: RequestInit & { apiToken: string }
): Promise<T> {
  const { apiToken, ...fetchOptions } = options;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${apiToken}`,
    ...(fetchOptions.headers as Record<string, string>),
  };
  if (!(fetchOptions.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }

  let resp = await fetch(`${API_BASE}${path}`, { ...fetchOptions, headers });

  // On 401, retry once with a force-refreshed token
  if (resp.status === 401 && _tokenRefreshFn) {
    try {
      const freshToken = await _tokenRefreshFn();
      headers.Authorization = `Bearer ${freshToken}`;
      resp = await fetch(`${API_BASE}${path}`, { ...fetchOptions, headers });
    } catch {
      // Token refresh failed — fall through to original error
    }
  }

  if (!resp.ok) {
    const detail = await resp.json().catch(() => ({ detail: resp.statusText }));
    throw new Error(detail.detail || `HTTP ${resp.status}`);
  }
  return resp.json();
}

// --- Receipt types ---
export interface ReceiptMetadata {
  receipt_id: string;
  file_name: string;
  upload_timestamp: string;
  source: string;
  blob_path: string;
  file_size_bytes: number;
  download_url: string;
  receipt_date?: string | null;
  sender_email?: string | null;
  status?: string;
  report_id?: string | null;
  email_subject?: string | null;
  vendor_name?: string | null;
  receipt_time?: string | null;
  receipt_amount?: number | null;
  is_directions?: boolean;
  total_mileage?: number | null;
  file_missing?: boolean;
  is_unattributed?: boolean;
}

export interface ExpenseLineItem {
  date: string;
  vendor: string;
  reason: string;
  category: string;
  amount: number | null;
  mileage: number | null;
  receipt_id?: string;
  is_round_trip?: boolean;
  route_id?: string;
  route_has_receipt?: boolean;
}

export interface ReportMetadata {
  report_id: string;
  title: string;
  generated_timestamp: string;
  date_range_start: string;
  date_range_end: string;
  total_amount: number;
  receipt_count: number;
  pdf_download_url: string;
  xlsx_download_url: string;
}

export interface MileageRoute {
    route_id: string;
    name: string;
    origin: string;
    destination: string;
    default_mileage: number;
    is_round_trip: boolean;
    receipt_blob_path: string;
    receipt_download_url: string;
}

// --- Receipts API ---
export async function uploadReceipt(apiToken: string, file: File): Promise<ReceiptMetadata> {
  const formData = new FormData();
  formData.append('file', file);
  return request('/receipts/upload', { method: 'POST', body: formData, apiToken });
}

/** Replace the underlying file for an existing receipt (recovery for orphaned receipts). */
export async function replaceReceiptFile(apiToken: string, receiptId: string, file: File): Promise<ReceiptMetadata> {
  const formData = new FormData();
  formData.append('file', file);
  return request(`/receipts/${receiptId}/replace-file`, { method: 'POST', body: formData, apiToken });
}

export async function listReceipts(apiToken: string): Promise<ReceiptMetadata[]> {
  return request('/receipts', { method: 'GET', apiToken });
}

export async function listUncategorizedReceipts(apiToken: string): Promise<ReceiptMetadata[]> {
  return request('/receipts/uncategorized', { method: 'GET', apiToken });
}

export async function listReceiptsByDateRange(
  apiToken: string,
  startDate: string,
  endDate: string,
  status?: string
): Promise<ReceiptMetadata[]> {
  const params = new URLSearchParams({
    date_start: startDate,
    date_end: endDate,
  });
  if (status) {
    params.set('status', status);
  }
  return request(`/receipts?${params}`, { method: 'GET', apiToken });
}

export async function claimReceipts(apiToken: string, receiptIds: string[]): Promise<ReceiptMetadata[]> {
  return request('/receipts/claim', {
    method: 'POST',
    body: JSON.stringify({ receipt_ids: receiptIds }),
    apiToken,
  });
}

export async function updateReceipt(
  apiToken: string,
  receiptId: string,
  updates: { receipt_amount?: number; vendor_name?: string; receipt_date?: string }
): Promise<ReceiptMetadata> {
  return request(`/receipts/${receiptId}`, { method: 'PATCH', body: JSON.stringify(updates), apiToken });
}

export async function deleteReceipt(apiToken: string, receiptId: string): Promise<void> {
  return request(`/receipts/${receiptId}`, { method: 'DELETE', apiToken });
}

// --- Reports API ---
export async function generateReport(
  apiToken: string,
  data: {
    employee_name: string;
    title: string;
    date_range_start: string;
    date_range_end: string;
    expenses: ExpenseLineItem[];
    receipt_ids: string[];
  }
): Promise<ReportMetadata> {
  return request('/reports/generate', {
    method: 'POST',
    body: JSON.stringify(data),
    apiToken,
  });
}

export async function listHistory(apiToken: string): Promise<ReportMetadata[]> {
  return request('/history', { method: 'GET', apiToken });
}

export async function listMileageRoutes(apiToken: string): Promise<MileageRoute[]> {
  return request('/mileage-routes', { method: 'GET', apiToken });
}

export async function saveMileageRoute(apiToken: string, route: { name: string; origin: string; destination: string; default_mileage: number; is_round_trip: boolean; receipt_id?: string }): Promise<MileageRoute> {
  return request('/mileage-routes', {
    method: 'POST',
    body: JSON.stringify(route),
    apiToken,
  });
}

export async function deleteMileageRoute(apiToken: string, routeId: string): Promise<void> {
  return request(`/mileage-routes/${routeId}`, { method: 'DELETE', apiToken });
}

export async function uploadRouteReceipt(apiToken: string, routeId: string, file: File): Promise<MileageRoute> {
  const formData = new FormData();
  formData.append('file', file);
  return request(`/mileage-routes/${routeId}/receipt`, { method: 'POST', body: formData, apiToken });
}

export async function downloadReport(
  apiToken: string,
  reportId: string,
  format: 'pdf' | 'xlsx' = 'pdf'
): Promise<void> {
  const resp = await fetch(`${API_BASE}/history/${reportId}/download?format=${format}`, {
    headers: { Authorization: `Bearer ${apiToken}` },
  });
  if (!resp.ok) {
    const detail = await resp.json().catch(() => ({ detail: resp.statusText }));
    throw new Error(detail.detail || `HTTP ${resp.status}`);
  }
  const blob = await resp.blob();
  const disposition = resp.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : `report.${format}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface ReportDetail {
  report: ReportMetadata;
  expenses: ExpenseLineItem[];
  receipts: ReceiptMetadata[];
  employee_name: string;
}

export async function deleteReport(apiToken: string, reportId: string): Promise<void> {
  return request(`/reports/${reportId}`, { method: 'DELETE', apiToken });
}

export async function getReportDetail(apiToken: string, reportId: string): Promise<ReportDetail> {
  return request(`/reports/${reportId}`, { method: 'GET', apiToken });
}

export function getReceiptPreviewUrl(apiToken: string, receiptId: string): string {
  return `${API_BASE}/receipts/${receiptId}/preview?token=${encodeURIComponent(apiToken)}`;
}

export function getAdminReceiptPreviewUrl(apiToken: string, receiptId: string): string {
  return `${API_BASE}/admin/receipts/${receiptId}/preview?token=${encodeURIComponent(apiToken)}`;
}

export function getRouteReceiptPreviewUrl(apiToken: string, routeId: string): string {
  return `${API_BASE}/mileage-routes/${routeId}/receipt/preview?token=${encodeURIComponent(apiToken)}`;
}

// --- Admin API ---
export interface AdminUser {
  email: string;
  name: string;
  added_by: string;
  added_timestamp: string;
}

export interface EventData {
  event_id: string;
  name: string;
  start_date: string;
  end_date: string;
  created_timestamp: string;
  auto_generated: boolean;
  report_count: number;
  total_amount: number;
}

export interface ReceiptDetail {
  receipt_id: string;
  vendor_name: string;
  receipt_date: string;
  receipt_amount: number;
  category: string;
  user_oid: string;
  user_name: string;
  event_id: string;
  event_name: string;
  status: string;
  file_name: string;
  blob_path?: string;
  download_url?: string;
}

export interface AccountingDashboard {
  year: number;
  date_range: { start: string; end: string };
  total_receipts: number;
  total_reports: number;
  total_amount: number;
  events: { event_id: string; name: string; start_date: string; end_date: string }[];
  years_with_data: number[];
  user_names: Record<string, string>;
  category_totals: Record<string, number>;
  receipts_by_month: Record<string, { total: number; count: number; by_user: Record<string, number>; by_category: Record<string, number> }>;
  receipts_by_user: Record<string, { total: number; count: number; by_category: Record<string, number> }>;
  receipts_by_event: Record<string, { total: number; count: number; by_category: Record<string, number> }>;
  receipt_details: ReceiptDetail[];
}

export async function checkAdmin(apiToken: string): Promise<{ is_admin: boolean }> {
  return request('/admin/check', { method: 'GET', apiToken });
}

export async function listAdmins(apiToken: string): Promise<AdminUser[]> {
  return request('/admin/admins', { method: 'GET', apiToken });
}

export async function addAdmin(apiToken: string, email: string, name: string): Promise<AdminUser> {
  return request('/admin/admins', { method: 'POST', body: JSON.stringify({ email, name }), apiToken });
}

export async function removeAdmin(apiToken: string, email: string): Promise<void> {
  return request(`/admin/admins/${encodeURIComponent(email)}`, { method: 'DELETE', apiToken });
}

export async function searchCgsUsers(apiToken: string, query: string): Promise<{ email: string; name: string }[]> {
  return request(`/admin/users/search?q=${encodeURIComponent(query)}`, { method: 'GET', apiToken });
}

export async function listEvents(apiToken: string): Promise<EventData[]> {
  return request('/admin/events', { method: 'GET', apiToken });
}

export async function createEvent(apiToken: string, data: { name: string; start_date: string; end_date: string }): Promise<EventData> {
  return request('/admin/events', { method: 'POST', body: JSON.stringify(data), apiToken });
}

export async function updateEvent(apiToken: string, eventId: string, data: { name?: string; start_date?: string; end_date?: string }): Promise<EventData> {
  return request(`/admin/events/${eventId}`, { method: 'PUT', body: JSON.stringify(data), apiToken });
}

export async function deleteEvent(apiToken: string, eventId: string): Promise<void> {
  return request(`/admin/events/${eventId}`, { method: 'DELETE', apiToken });
}

export async function autoGenerateEvents(apiToken: string): Promise<{ events_created: number; events: EventData[] }> {
  return request('/admin/events/auto-generate', { method: 'POST', apiToken });
}

export async function getAccountingDashboard(
  apiToken: string,
  filters?: { year?: number; date_start?: string; date_end?: string; event_id?: string; user_filter?: string }
): Promise<AccountingDashboard> {
  const params = new URLSearchParams();
  if (filters?.year) params.set('year', String(filters.year));
  if (filters?.date_start) params.set('date_start', filters.date_start);
  if (filters?.date_end) params.set('date_end', filters.date_end);
  if (filters?.event_id) params.set('event_id', filters.event_id);
  if (filters?.user_filter) params.set('user_filter', filters.user_filter);
  const qs = params.toString();
  return request(`/admin/dashboard${qs ? `?${qs}` : ''}`, { method: 'GET', apiToken });
}

export async function updateReport(
  apiToken: string,
  reportId: string,
  data: {
    employee_name: string;
    title: string;
    date_range_start: string;
    date_range_end: string;
    expenses: ExpenseLineItem[];
    receipt_ids: string[];
  }
): Promise<ReportMetadata> {
  return request(`/reports/${reportId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    apiToken,
  });
}

// --- Admin Reports ---
export interface AdminReport {
  report_id: string;
  title: string;
  employee_name: string;
  date_range_start: string;
  date_range_end: string;
  total_amount: number;
  receipt_count: number;
  generated_timestamp: string;
  user_oid: string;
  user_name: string;
  event_id: string;
}

export async function listAdminReports(apiToken: string): Promise<AdminReport[]> {
  return request('/admin/reports', { method: 'GET', apiToken });
}

// --- Logs API ---
export interface LogEntry {
  timestamp: string;
  level: string;
  user: string;
  module: string;
  function_name: string;
  message: string;
  metadata: string;
}

export interface LogSettings {
  capture_level: string;
}

export async function getLogs(apiToken: string, filters?: {
  level?: string; log_user?: string; module?: string; function_name?: string;
  date_start?: string; date_end?: string; search?: string; limit?: number;
}): Promise<LogEntry[]> {
  const params = new URLSearchParams();
  if (filters?.level) params.set('level', filters.level);
  if (filters?.log_user) params.set('log_user', filters.log_user);
  if (filters?.module) params.set('module', filters.module);
  if (filters?.function_name) params.set('function_name', filters.function_name);
  if (filters?.date_start) params.set('date_start', filters.date_start);
  if (filters?.date_end) params.set('date_end', filters.date_end);
  if (filters?.search) params.set('search', filters.search);
  if (filters?.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return request(`/admin/logs${qs ? `?${qs}` : ''}`, { method: 'GET', apiToken });
}

export async function getLogSettings(apiToken: string): Promise<LogSettings> {
  return request('/admin/logs/settings', { method: 'GET', apiToken });
}

export async function getEmailProcessingStatus(apiToken: string): Promise<{processing: boolean}> {
  return request('/webhooks/email-processing-status', { method: 'GET', apiToken });
}

export async function setLogSettings(apiToken: string, captureLevel: string): Promise<LogSettings> {
  return request('/admin/logs/settings', {
    method: 'PUT',
    body: JSON.stringify({ capture_level: captureLevel }),
    apiToken,
  });
}

export async function assignReportToEvent(apiToken: string, reportId: string, eventId: string): Promise<any> {
  return request(`/admin/reports/${reportId}/assign-event`, {
    method: 'POST',
    body: JSON.stringify({ event_id: eventId }),
    apiToken,
  });
}

export interface AppSettings {
  mileage_rate: number;
}

export async function getAppSettings(apiToken: string): Promise<AppSettings> {
  return request('/admin/settings', { method: 'GET', apiToken });
}

export async function updateAppSettings(apiToken: string, settings: AppSettings): Promise<AppSettings> {
  return request('/admin/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
    apiToken,
  });
}
